import * as React from 'react'

export type Tags = keyof React.JSX.IntrinsicElements
export type CustomComponent = React.FunctionComponent<any>
export type AsTarget = Tags | CustomComponent

/**
 * Props of a polymorphic component :
 * - if AS is a tag component, use its props (so `<Polymorphic.a as="input" />`
 *   will typecheck props like an input tag)
 * - if AS is a custom component (function or class), combine the props of TAG
 *   and AS with precedence on AS (so `<Polymorphic.input as={MyComponent} />
 *   will typecheck like an input tag with props of `MyComponent` overriding
 *   those of the input)
 * - if no AS was provided, use the props of TAG
 *
 * @deprecated
 */
export type PolymorphicProps<TAG extends Tags, AS extends AsTarget = TAG> = Substitute<
  React.ComponentPropsWithoutRef<TAG>,
  React.ComponentPropsWithoutRef<AS extends AsTarget ? AS : React.FunctionComponent>
> & { as?: AS | undefined }

/** @deprecated */
export interface PolymorphicComponent<TAG extends Tags, PROPS extends object = {}> {
  <AS extends AsTarget = TAG>(
    props: PolymorphicProps<TAG, AS> & PROPS & { ref?: React.LegacyRef<any> },
  ): React.ReactElement<PolymorphicProps<TAG, AS> & PROPS>
  (
    props: React.ComponentPropsWithoutRef<TAG> & PROPS & { ref?: React.LegacyRef<any> },
  ): React.ReactElement<React.ComponentPropsWithoutRef<TAG> & PROPS>
  displayName?: string
}

/**
 * Factory to create a polymorphic version of a given html tag. The component
 * takes an extra `as` props that "morph" it to the target component (either
 * another html tag or a custom component)
 */
function polymorphicFactory<TAG extends keyof React.JSX.IntrinsicElements>(tag: TAG) {
  const component = React.forwardRef(({ as, ...props }: { as?: AsTarget }, ref) => {
    const Tag = as ?? tag
    return <Tag {...(props as any)} ref={ref} />
  }) as PolymorphicComponent<TAG>

  component.displayName = `styled.${tag}`

  return component
}

type FastOmit<T extends object, U extends string | number | symbol> = {
  [K in keyof T as K extends U ? never : K]: T[K]
}
type Substitute<A extends object, B extends object> = FastOmit<A, keyof B> & B

/** @deprecated */
export const Polymorphic = {
  /** @deprecated */
  div: polymorphicFactory('div'),
  /** @deprecated */
  span: polymorphicFactory('span'),
  /** @deprecated */
  section: polymorphicFactory('section'),
  /** @deprecated */
  input: polymorphicFactory('input'),
  /** @deprecated */
  a: polymorphicFactory('a'),
  /** @deprecated */
  p: polymorphicFactory('p'),
  /** @deprecated */
  button: polymorphicFactory('button'),
  /** @deprecated */
  img: polymorphicFactory('img'),
  /** @deprecated */
  h6: polymorphicFactory('h6'),
}
